/**
 * FileLink Component
 * A file reference input component with search, open, and clear buttons
 * 
 * @description Provides a styled file reference input for document management:
 * - Search (magnifying glass): Open file picker, upload to server, store reference
 * - Open (folder): Open file in document editor (docx/xlsx) or image viewer
 * - Clear (X): Remove the file reference
 * 
 * This is the standard pattern for handling file references in Apex/TriSys.
 * 
 * @example
 * ```typescript
 * const fileLink = new FileLink({
 *   id: 'identification-link1',
 *   label: 'Link 1',
 *   tableName: 'ContactConfigFields',
 *   tableFieldName: 'IdentificationLink',
 *   color: 'info',
 *   onFileChange: (path) => console.log('File changed:', path)
 * });
 * await fileLink.initialize();
 * const container = document.getElementById('link1-container');
 * container.appendChild(fileLink.getElement());
 * ```
 */

import { IComponent } from './IComponent';
import { Logger } from '@/services/Logger';
import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { trisysAPI } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('FileLink');

/**
 * Configuration options for the FileLink component
 */
export interface FileLinkOptions {
  /** Unique ID for the input element */
  id: string;
  /** Optional label displayed above the input */
  label?: string;
  /** Database table name for file storage (e.g., "ContactConfigFields") */
  tableName: string;
  /** Database field name for file storage (e.g., "IdentificationLink") */
  tableFieldName: string;
  /** Color variant for buttons */
  color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is readonly (no search/clear, only open) */
  readonly?: boolean;
  /** Accepted file types (default: all) */
  accept?: string;
  /** Callback when file changes */
  onFileChange?: (path: string | null) => void;
  /** Callback when file is opened */
  onFileOpen?: (path: string) => void;
  /** Custom open handler (overrides default behavior) */
  customOpenHandler?: (path: string) => Promise<void>;
  /** Show label above input */
  showLabel?: boolean;
  /** Additional CSS classes for the container */
  containerClass?: string;
}

/**
 * FileLink Component - File reference input with search/open/clear buttons
 */
export class FileLink implements IComponent {
  private options: FileLinkOptions;
  private element: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private filePath: string | null = null;
  private hiddenFileInput: HTMLInputElement | null = null;

  constructor(options: FileLinkOptions) {
    this.options = {
      color: 'info',
      size: 'sm',
      disabled: false,
      readonly: false,
      accept: '*/*',
      showLabel: false,
      ...options
    };
  }

  /**
   * Initialize the component
   */
  public async initialize(): Promise<void> {
    this.element = this.render();
    this.bindEvents();
    logger.debug('FileLink initialized', { id: this.options.id });
  }

  /**
   * Get the component's root element
   */
  public getElement(): HTMLElement {
    if (!this.element) {
      throw new Error('FileLink not initialized. Call initialize() first.');
    }
    return this.element;
  }

  /**
   * Render into a container element
   */
  public renderTo(container: HTMLElement): void {
    if (!this.element) {
      throw new Error('FileLink not initialized. Call initialize() first.');
    }
    container.appendChild(this.element);
  }

  /**
   * Get the current file path
   */
  public getFilePath(): string | null {
    return this.filePath;
  }

  /**
   * Set the file path
   */
  public setFilePath(path: string | null): void {
    this.filePath = path;
    if (this.inputElement) {
      this.inputElement.value = path ? this.extractFilename(path) : '';
      this.inputElement.setAttribute('data-file-path', path || '');
    }
    this.updateButtonStates();
  }

  /**
   * Get the filename from a path
   */
  public getFilename(): string {
    return this.inputElement?.value || '';
  }

  /**
   * Check if a file is set
   */
  public hasFile(): boolean {
    return !!this.filePath;
  }

  /**
   * Enable/disable the component
   */
  public setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.updateButtonStates();
  }

  /**
   * Destroy the component and clean up
   */
  public destroy(): void {
    if (this.hiddenFileInput) {
      this.hiddenFileInput.remove();
    }
    if (this.element) {
      this.element.remove();
    }
    this.element = null;
    this.inputElement = null;
    this.hiddenFileInput = null;
  }

  /**
   * Render the component HTML
   */
  private render(): HTMLElement {
    const { id, label, color, size, disabled, readonly, showLabel, containerClass } = this.options;

    const container = document.createElement('div');
    container.className = `file-link-container ${containerClass || ''}`;

    // Label (if shown)
    if (showLabel && label) {
      const labelElement = document.createElement('label');
      labelElement.className = 'label py-0.5';
      labelElement.innerHTML = `<span class="label-text text-xs font-medium">${label}</span>`;
      container.appendChild(labelElement);
    }

    // Input group with buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'join w-full';

    // Readonly filename input
    const input = document.createElement('input');
    input.type = 'text';
    input.id = id;
    input.className = `input input-bordered input-${size} join-item flex-1`;
    input.readOnly = true;
    input.placeholder = label || 'No file selected';
    this.inputElement = input;
    buttonGroup.appendChild(input);

    // Search button (find file)
    if (!readonly) {
      const searchBtn = document.createElement('button');
      searchBtn.type = 'button';
      searchBtn.className = `btn btn-${size} join-item file-link-search bg-base-100 hover:bg-base-200 border border-base-content/20`;
      searchBtn.innerHTML = '<i class="fas fa-search" style="color: #3b82f6;"></i>';
      searchBtn.title = 'Find file';
      searchBtn.disabled = disabled || false;
      buttonGroup.appendChild(searchBtn);
    }

    // Open button - green icon when enabled, grey when disabled
    // Don't use disabled attribute - handle via class and event prevention to avoid DaisyUI disabled border styling
    const openBtn = document.createElement('button');
    openBtn.type = 'button';
    const openDisabled = disabled || !this.filePath;
    openBtn.className = `btn btn-${size} join-item file-link-open bg-base-100 border border-base-content/20 ${openDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
    openBtn.innerHTML = `<i class="fas fa-folder-open" style="color: ${openDisabled ? '#9ca3af' : '#22c55e'};"></i>`;
    openBtn.title = 'Open file';
    openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
    buttonGroup.appendChild(openBtn);

    // Clear button - red icon when enabled, grey when disabled
    if (!readonly) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      const clearDisabled = disabled || !this.filePath;
      clearBtn.className = `btn btn-${size} join-item file-link-clear bg-base-100 border border-base-content/20 ${clearDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
      clearBtn.innerHTML = `<i class="fas fa-times" style="color: ${clearDisabled ? '#9ca3af' : '#ef4444'};"></i>`;
      clearBtn.title = 'Clear file';
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      buttonGroup.appendChild(clearBtn);
    }

    container.appendChild(buttonGroup);

    // Hidden file input for file picker
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'file';
    hiddenInput.id = `${id}-file-picker`;
    hiddenInput.className = 'hidden';
    hiddenInput.accept = this.options.accept || '*/*';
    this.hiddenFileInput = hiddenInput;
    container.appendChild(hiddenInput);

    return container;
  }

  /**
   * Bind event handlers
   */
  private bindEvents(): void {
    if (!this.element) return;

    // Search button click
    const searchBtn = this.element.querySelector('.file-link-search');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => this.handleSearch());
    }

    // Open button click
    const openBtn = this.element.querySelector('.file-link-open');
    if (openBtn) {
      openBtn.addEventListener('click', () => this.handleOpen());
    }

    // Clear button click
    const clearBtn = this.element.querySelector('.file-link-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClear());
    }

    // Hidden file input change
    if (this.hiddenFileInput) {
      this.hiddenFileInput.addEventListener('change', (e) => this.handleFileSelected(e));
    }
  }

  /**
   * Handle search button click - open file picker
   */
  private handleSearch(): void {
    if (this.options.disabled) return;
    
    logger.debug('FileLink search clicked', { id: this.options.id });
    this.hiddenFileInput?.click();
  }

  /**
   * Handle file selected from picker
   */
  private async handleFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    if (!file) return;

    logger.debug('File selected', { 
      id: this.options.id, 
      fileName: file.name, 
      size: file.size,
      type: file.type 
    });

    try {
      ToasterService.info(`Uploading ${file.name}...`);

      // Step 1: Upload file to temporary storage
      const uploadResult = await trisysAPI.uploadFile(file);
      
      if (!uploadResult || uploadResult.length === 0) {
        ToasterService.error('File upload failed');
        return;
      }

      logger.debug('File uploaded to temp storage', { paths: uploadResult });

      // Step 2: Move to permanent storage
      const moveResult = await trisysAPI.moveUploadedFile({
        UploadedFilePath: uploadResult[0],
        TableName: this.options.tableName,
        TableFieldName: this.options.tableFieldName
      });

      if (!moveResult.Success) {
        ToasterService.error(`Failed to save file: ${moveResult.ErrorMessage || 'Unknown error'}`);
        return;
      }

      logger.info('File moved to permanent storage', { 
        permanentPath: moveResult.FolderFilePath 
      });

      // Step 3: Update the input with the new path
      this.setFilePath(moveResult.FolderFilePath);
      
      // Step 4: Notify callback
      this.options.onFileChange?.(moveResult.FolderFilePath);

      ToasterService.success(`${file.name} uploaded successfully`);
    } catch (error) {
      logger.error('File upload failed', { error });
      ToasterService.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Reset the file input so the same file can be selected again
      input.value = '';
    }
  }

  /**
   * Handle open button click - open file for viewing/editing
   */
  private async handleOpen(): Promise<void> {
    if (!this.filePath) {
      ToasterService.warning('No file to open');
      return;
    }

    logger.debug('FileLink open clicked', { id: this.options.id, path: this.filePath });

    // If custom handler provided, use it
    if (this.options.customOpenHandler) {
      await this.options.customOpenHandler(this.filePath);
      return;
    }

    // Determine file type and open accordingly
    const extension = this.getFileExtension(this.filePath).toLowerCase();
    
    try {
      if (this.isImageFile(extension)) {
        // Open image in modal viewer
        await this.openImageViewer(this.filePath);
      } else if (this.isDocumentFile(extension)) {
        // Open document in Apex document editor
        await this.openDocumentEditor(this.filePath);
      } else {
        // Generic file - try document editor
        await this.openDocumentEditor(this.filePath);
      }

      this.options.onFileOpen?.(this.filePath);
    } catch (error) {
      logger.error('Failed to open file', { path: this.filePath, error });
      ToasterService.error('Failed to open file');
    }
  }

  /**
   * Handle clear button click - remove file reference
   */
  private handleClear(): void {
    if (this.options.disabled) return;
    if (!this.filePath) return;

    logger.debug('FileLink clear clicked', { id: this.options.id });

    ModalSystem.questionYesNo(
      'Are you sure you want to remove this file reference?',
      'Clear File',
      () => {
        const oldPath = this.filePath;
        this.setFilePath(null);
        this.options.onFileChange?.(null);
        ToasterService.info('File reference cleared');
        logger.info('File reference cleared', { id: this.options.id, oldPath });
      },
      () => {
        // User cancelled
      }
    );
  }

  /**
   * Open image in modal viewer
   */
  private async openImageViewer(path: string): Promise<void> {
    // Use getFileUrl API to get a web-accessible URL
    const fileResponse = await trisysAPI.getFileUrl(path);
    
    if (!fileResponse.Success || !fileResponse.URL) {
      ToasterService.error(`Failed to load image: ${fileResponse.ErrorMessage || 'Unknown error'}`);
      return;
    }

    const filename = fileResponse.FileName || this.extractFilename(path);
    const imageUrl = fileResponse.URL;
    
    // Create image modal
    const modalHtml = `
      <div class="flex items-center justify-center p-4" style="max-height: 80vh;">
        <img 
          src="${imageUrl}" 
          alt="${filename}"
          class="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
          onerror="this.src='data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>📄</text></svg>'"
        />
      </div>
    `;

    ModalSystem.show({
      title: `<i class="fas fa-image text-success mr-2"></i>${filename}`,
      html: modalHtml,
      width: 'w-auto max-w-[90vw]',
      buttons: [
        {
          caption: 'Download',
          className: 'btn-primary',
          callback: () => {
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = filename;
            link.click();
          }
        },
        {
          caption: 'Open in New Tab',
          className: 'btn-secondary',
          callback: () => {
            window.open(imageUrl, '_blank');
          }
        },
        {
          caption: 'Close',
          className: 'btn-ghost'
        }
      ]
    });
  }

  /**
   * Open document in Apex document editor
   */
  private async openDocumentEditor(path: string): Promise<void> {
    ToasterService.info('Opening document...');

    try {
      const editorUrl = await trisysAPI.getDocumentEditorUrl(path);

      if (!editorUrl) {
        ToasterService.error('Failed to open document. Could not generate editor URL.');
        return;
      }

      const filename = this.extractFilename(path);

      // Create document editor modal
      const modalHtml = `
        <div class="flex flex-col" style="height: 85vh;">
          <iframe 
            src="${editorUrl}"
            class="flex-1 w-full border-0 rounded"
            allowfullscreen="true"
            frameborder="0"
            allowtransparency="true"
            scrolling="no"
          ></iframe>
        </div>
      `;

      ModalSystem.show({
        title: `<i class="fas fa-file-alt text-primary mr-2"></i>${filename}`,
        html: modalHtml,
        width: 'w-[90vw] max-w-[90vw]',
        buttons: [
          {
            caption: 'Open Full Screen',
            className: 'btn-secondary',
            callback: () => {
              window.open(editorUrl, '_blank');
            }
          },
          {
            caption: 'Close',
            className: 'btn-ghost'
          }
        ]
      });
    } catch (error) {
      logger.error('Failed to open document editor', { path, error });
      ToasterService.error('Failed to open document');
    }
  }

  /**
   * Update button disabled states based on current state
   */
  private updateButtonStates(): void {
    if (!this.element) return;

    const { disabled } = this.options;
    const hasFile = !!this.filePath;

    const searchBtn = this.element.querySelector('.file-link-search') as HTMLButtonElement;
    const openBtn = this.element.querySelector('.file-link-open') as HTMLButtonElement;
    const clearBtn = this.element.querySelector('.file-link-clear') as HTMLButtonElement;

    if (searchBtn) searchBtn.disabled = disabled || false;
    
    if (openBtn) {
      const openDisabled = disabled || !hasFile;
      // Use dataset instead of disabled attribute to avoid DaisyUI border styling
      openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
      // Update styling based on state
      if (openDisabled) {
        openBtn.classList.add('cursor-not-allowed', 'opacity-60');
        openBtn.classList.remove('hover:bg-base-200');
      } else {
        openBtn.classList.remove('cursor-not-allowed', 'opacity-60');
        openBtn.classList.add('hover:bg-base-200');
      }
      // Update icon color based on state - use inline style for visibility
      const openIcon = openBtn.querySelector('i');
      if (openIcon) {
        openIcon.className = 'fas fa-folder-open';
        (openIcon as HTMLElement).style.color = openDisabled ? '#9ca3af' : '#22c55e';
      }
    }
    
    if (clearBtn) {
      const clearDisabled = disabled || !hasFile;
      // Use dataset instead of disabled attribute to avoid DaisyUI border styling
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      // Update styling based on state
      if (clearDisabled) {
        clearBtn.classList.add('cursor-not-allowed', 'opacity-60');
        clearBtn.classList.remove('hover:bg-base-200');
      } else {
        clearBtn.classList.remove('cursor-not-allowed', 'opacity-60');
        clearBtn.classList.add('hover:bg-base-200');
      }
      // Update icon color based on state - use inline style for visibility
      const clearIcon = clearBtn.querySelector('i');
      if (clearIcon) {
        clearIcon.className = 'fas fa-times';
        (clearIcon as HTMLElement).style.color = clearDisabled ? '#9ca3af' : '#ef4444';
      }
    }
  }

  /**
   * Extract filename from a file path
   */
  private extractFilename(path: string): string {
    if (!path) return '';
    // Handle both forward and back slashes
    const parts = path.replace(/\\/g, '/').split('/');
    return parts[parts.length - 1] || '';
  }

  /**
   * Get file extension from path
   */
  private getFileExtension(path: string): string {
    const filename = this.extractFilename(path);
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(lastDot + 1) : '';
  }

  /**
   * Check if file is an image
   */
  private isImageFile(extension: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
    return imageExtensions.includes(extension.toLowerCase());
  }

  /**
   * Check if file is a document (office/pdf)
   */
  private isDocumentFile(extension: string): boolean {
    const docExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'txt', 'rtf'];
    return docExtensions.includes(extension.toLowerCase());
  }
}

/**
 * Factory function to create a FileLink from existing HTML elements
 * Useful for binding to HTML already in the page
 */
export function bindFileLink(
  inputId: string,
  options: Omit<FileLinkOptions, 'id'>
): FileLink {
  const fileLink = new FileLink({ id: inputId, ...options });
  return fileLink;
}

/**
 * Configuration for binding existing HTML file link elements
 */
export interface ExistingFileLinkBinding {
  /** The input element ID */
  inputId: string;
  /** Search button selector or element */
  searchButton?: string | HTMLElement;
  /** Open button selector or element */
  openButton?: string | HTMLElement;
  /** Clear button selector or element */
  clearButton?: string | HTMLElement;
  /** Database table name */
  tableName: string;
  /** Database field name */
  tableFieldName: string;
  /** Accepted file types */
  accept?: string;
  /** Callback when file changes */
  onFileChange?: (path: string | null) => void;
  /** Callback when file opens */
  onFileOpen?: (path: string) => void;
}

/**
 * Bind file link functionality to existing HTML elements.
 * Use this when the HTML is already rendered and you just need to add behavior.
 */
export class FileLinkBinder {
  private options: ExistingFileLinkBinding;
  private inputElement: HTMLInputElement | null = null;
  private hiddenFileInput: HTMLInputElement | null = null;
  private filePath: string | null = null;

  constructor(options: ExistingFileLinkBinding) {
    this.options = options;
  }

  /**
   * Initialize the binder - find elements and attach event handlers
   */
  public async initialize(): Promise<void> {
    // Find the input element
    this.inputElement = document.getElementById(this.options.inputId) as HTMLInputElement;
    if (!this.inputElement) {
      logger.warn('FileLinkBinder: Input element not found', { inputId: this.options.inputId });
      return;
    }

    // Create hidden file input for file picker
    this.hiddenFileInput = document.createElement('input');
    this.hiddenFileInput.type = 'file';
    this.hiddenFileInput.className = 'hidden';
    this.hiddenFileInput.accept = this.options.accept || '*/*';
    this.hiddenFileInput.id = `${this.options.inputId}-file-picker`;
    document.body.appendChild(this.hiddenFileInput);

    // Bind hidden file input change
    this.hiddenFileInput.addEventListener('change', (e) => this.handleFileSelected(e));

    // Bind search button
    const searchBtn = this.resolveElement(this.options.searchButton);
    if (searchBtn) {
      searchBtn.addEventListener('click', () => this.handleSearch());
    }

    // Bind open button
    const openBtn = this.resolveElement(this.options.openButton);
    if (openBtn) {
      openBtn.addEventListener('click', () => this.handleOpen());
    }

    // Bind clear button
    const clearBtn = this.resolveElement(this.options.clearButton);
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClear());
    }

    // Get initial path from data attribute if present
    const initialPath = this.inputElement.getAttribute('data-file-path');
    if (initialPath) {
      this.filePath = initialPath;
    }

    logger.debug('FileLinkBinder initialized', { inputId: this.options.inputId });
  }

  /**
   * Resolve a selector or element to an HTMLElement
   */
  private resolveElement(selectorOrElement?: string | HTMLElement): HTMLElement | null {
    if (!selectorOrElement) return null;
    if (typeof selectorOrElement === 'string') {
      return document.querySelector(selectorOrElement);
    }
    return selectorOrElement;
  }

  /**
   * Get the current file path
   */
  public getFilePath(): string | null {
    return this.filePath;
  }

  /**
   * Set the file path and update the input display
   */
  public setFilePath(path: string | null): void {
    this.filePath = path;
    if (this.inputElement) {
      this.inputElement.value = path ? this.extractFilename(path) : '';
      this.inputElement.setAttribute('data-file-path', path || '');
    }
  }

  /**
   * Handle search button click
   */
  private handleSearch(): void {
    logger.debug('FileLinkBinder search clicked', { inputId: this.options.inputId });
    this.hiddenFileInput?.click();
  }

  /**
   * Handle file selected from picker
   */
  private async handleFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    if (!file) return;

    logger.debug('File selected', { 
      inputId: this.options.inputId, 
      fileName: file.name, 
      size: file.size 
    });

    try {
      ToasterService.info(`Uploading ${file.name}...`);

      // Step 1: Upload file to temporary storage
      const uploadResult = await trisysAPI.uploadFile(file);
      
      if (!uploadResult || uploadResult.length === 0) {
        ToasterService.error('File upload failed');
        return;
      }

      // Step 2: Move to permanent storage
      const moveResult = await trisysAPI.moveUploadedFile({
        UploadedFilePath: uploadResult[0],
        TableName: this.options.tableName,
        TableFieldName: this.options.tableFieldName
      });

      if (!moveResult.Success) {
        ToasterService.error(`Failed to save file: ${moveResult.ErrorMessage || 'Unknown error'}`);
        return;
      }

      // Step 3: Update the input
      this.setFilePath(moveResult.FolderFilePath);
      
      // Step 4: Notify callback
      this.options.onFileChange?.(moveResult.FolderFilePath);

      ToasterService.success(`${file.name} uploaded successfully`);
    } catch (error) {
      logger.error('File upload failed', { error });
      ToasterService.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      input.value = '';
    }
  }

  /**
   * Handle open button click
   */
  private async handleOpen(): Promise<void> {
    if (!this.filePath) {
      ToasterService.warning('No file to open');
      return;
    }

    logger.debug('FileLinkBinder open clicked', { inputId: this.options.inputId, path: this.filePath });

    const extension = this.getFileExtension(this.filePath).toLowerCase();
    
    try {
      if (this.isImageFile(extension)) {
        await this.openImageViewer(this.filePath);
      } else {
        await this.openDocumentEditor(this.filePath);
      }

      this.options.onFileOpen?.(this.filePath);
    } catch (error) {
      logger.error('Failed to open file', { path: this.filePath, error });
      ToasterService.error('Failed to open file');
    }
  }

  /**
   * Handle clear button click
   */
  private handleClear(): void {
    if (!this.filePath) return;

    logger.debug('FileLinkBinder clear clicked', { inputId: this.options.inputId });

    ModalSystem.questionYesNo(
      'Are you sure you want to remove this file reference?',
      'Clear File',
      () => {
        const oldPath = this.filePath;
        this.setFilePath(null);
        this.options.onFileChange?.(null);
        ToasterService.info('File reference cleared');
        logger.info('File reference cleared', { inputId: this.options.inputId, oldPath });
      },
      () => {
        // User cancelled
      }
    );
  }

  /**
   * Open image in modal viewer
   */
  private async openImageViewer(path: string): Promise<void> {
    logger.debug('Opening image viewer', { path });
    
    try {
      // Use getFileUrl API to get a web-accessible URL
      const fileResponse = await trisysAPI.getFileUrl(path);
      
      logger.debug('GetFileURL result', { 
        path, 
        fileName: fileResponse.FileName,
        url: fileResponse.URL, 
        success: fileResponse.Success,
        error: fileResponse.ErrorMessage 
      });
      
      if (!fileResponse.Success || !fileResponse.URL) {
        ToasterService.error(`Failed to load image: ${fileResponse.ErrorMessage || 'Unknown error'}`);
        return;
      }

      const filename = fileResponse.FileName || this.extractFilename(path);
      const imageUrl = fileResponse.URL;
      
      logger.info('Displaying image in modal', { filename, imageUrl });

      ModalSystem.show({
        title: `<i class="fas fa-image text-success mr-2"></i>${filename}`,
        html: `
          <div class="flex flex-col items-center justify-center p-4" style="max-height: 80vh;">
            <img 
              src="${imageUrl}" 
              alt="${filename}"
              class="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              onerror="this.parentElement.innerHTML = '<div class=\\'alert alert-error\\'><i class=\\'fas fa-exclamation-circle mr-2\\'></i>Failed to load image</div>'"
            />
          </div>
        `,
        width: 'w-auto max-w-[90vw]',
        buttons: [
          {
            caption: 'Download',
            className: 'btn-primary',
            callback: () => {
              const link = document.createElement('a');
              link.href = imageUrl;
              link.download = filename;
              link.click();
            }
          },
          {
            caption: 'Close',
            className: 'btn-ghost'
          }
        ]
      });
    } catch (error) {
      logger.error('Failed to open image viewer', { path, error });
      ToasterService.error('Failed to open image viewer');
    }
  }

  /**
   * Open document in Apex editor
   */
  private async openDocumentEditor(path: string): Promise<void> {
    ToasterService.info('Opening document...');

    const editorUrl = await trisysAPI.getDocumentEditorUrl(path);

    if (!editorUrl) {
      ToasterService.error('Failed to open document.');
      return;
    }

    const filename = this.extractFilename(path);

    ModalSystem.show({
      title: `<i class="fas fa-file-alt text-primary mr-2"></i>${filename}`,
      html: `
        <div class="flex flex-col" style="height: 85vh;">
          <iframe 
            src="${editorUrl}"
            class="flex-1 w-full border-0 rounded"
            allowfullscreen="true"
          ></iframe>
        </div>
      `,
      width: 'w-[90vw] max-w-[90vw]',
      buttons: [
        {
          caption: 'Open Full Screen',
          className: 'btn-secondary',
          callback: () => { window.open(editorUrl, '_blank'); }
        },
        {
          caption: 'Close',
          className: 'btn-ghost'
        }
      ]
    });
  }

  private extractFilename(path: string): string {
    if (!path) return '';
    const parts = path.replace(/\\/g, '/').split('/');
    return parts[parts.length - 1] || '';
  }

  private getFileExtension(path: string): string {
    const filename = this.extractFilename(path);
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(lastDot + 1) : '';
  }

  private isImageFile(extension: string): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension.toLowerCase());
  }

  /**
   * Destroy and clean up
   */
  public destroy(): void {
    if (this.hiddenFileInput) {
      this.hiddenFileInput.remove();
    }
  }
}

/**
 * Convenience function to bind multiple file links at once.
 * Uses data-link attribute pattern from contactDetail.html
 */
export async function bindIdentificationLinks(
  linkConfig: Array<{
    linkNumber: number;
    tableFieldName: string;
    onFileChange?: (path: string | null) => void;
  }>
): Promise<FileLinkBinder[]> {
  const binders: FileLinkBinder[] = [];

  for (const config of linkConfig) {
    // Use the standard field ID pattern: ContactConfigFields_<tableFieldName>
    const inputId = `ContactConfigFields_${config.tableFieldName}`;
    const binder = new FileLinkBinder({
      inputId,
      searchButton: `.id-link-search[data-link="${config.linkNumber}"]`,
      openButton: `.id-link-open[data-link="${config.linkNumber}"]`,
      clearButton: `.id-link-clear[data-link="${config.linkNumber}"]`,
      tableName: 'ContactConfigFields',
      tableFieldName: config.tableFieldName,
      onFileChange: config.onFileChange
    });
    await binder.initialize();
    binders.push(binder);
  }

  return binders;
}
